import { useRequirePermissions } from '@/shared/hooks';
import type { UserDto } from '@/shared/types';
import { SearchFilterField, SelectFilterField } from '@/shared/ui';
import { Page, PageHeader } from '@/shared/ui/page';
import { toaster } from '@/shared/ui/toaster.component';
import { f, useFilters } from '@/shared/utils';
import { formatRegisterNumber } from '@/shared/utils/format-register-number';
import { getRoleLabel } from '@/shared/utils/get-role-label';
import { getUserStatus } from '@/shared/utils/get-user-status';
import {
  Avatar,
  Button,
  cn,
  DataTable,
  DataTableColumn,
  Menu,
  Tag,
} from '@noorden-insurtech/alba-design-system';
import { Ban, Edit, Mail, Plus, UserCheck } from 'lucide-react';
import { useMemo, useState } from 'react';
import { EditUserModal } from '../../users/components/edit-user.modal';
import { useActivateUser, useDeactivateUser } from '../../users/hooks';
import { useBrokerDetails, useBrokersUsers } from '../hooks';
import { brokerActionBlockedReason } from '../utils/broker-status.util';
import { CreateBrokerUserModal } from './create-broker-user.modal';

const PAGE_SIZE_OPTIONS = [5, 10, 20, 30, 40, 50];

const profileOptions = [
  { label: 'Todos', value: '' },
  { label: 'Corretor', value: 'broker.broker' },
  { label: 'Master', value: 'broker.master' },
];

interface BrokerUsersListProps {
  brokerId: string;
}

export function BrokerUsersList({ brokerId }: BrokerUsersListProps) {
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState<UserDto | null>(null);

  const { hasPermission: canManageUsers } = useRequirePermissions({
    permissions: ['parties.brokers.users.write'],
  });

  const { hasPermission: canManageStatus } = useRequirePermissions({
    permissions: ['identity.users.write', 'parties.brokers.users.write'],
  });

  // A Api recusa criar usuário em corretora inativa; aqui só evitamos o clique.
  const brokerDetails = useBrokerDetails(brokerId);
  const createUserBlockedReason = brokerActionBlockedReason(
    brokerDetails,
    'adicionar usuários'
  );

  const { filters, setFilters, registerFilter } = useFilters({
    search: f.string().withDefault('').withDebounce(500),
    status: f.string().withDefault(''),
    page: f.integer().withDefault(1),
    pageSize: f.integer().withDefault(5),
  });

  const {
    data: usersData,
    isLoading,
    error,
    refetch,
  } = useBrokersUsers(brokerId, {
    filter: filters.search || undefined,
    pageSize: filters.pageSize,
    pageNumber: filters.page,
  });

  const { mutateAsync: deactivateUser, isPending: isDeactivating } =
    useDeactivateUser();
  const { mutateAsync: activateUser, isPending: isActivating } =
    useActivateUser();

  const handleDeactivateUser = async (email: string) => {
    try {
      await deactivateUser(email);
      toaster.create({
        type: 'success',
        title: 'Usuário inativado com sucesso!',
        description:
          'O usuário não poderá mais acessar o Portal até nova ativação.',
      });
      await refetch();
    } catch (err) {
      toaster.create({
        type: 'error',
        title: 'Erro ao desativar usuário',
        description:
          err instanceof Error
            ? err.message
            : 'Não foi possível concluir a ação.',
      });
    }
  };

  const handleActivateUser = async (email: string) => {
    try {
      await activateUser(email);
      toaster.create({
        type: 'success',
        title: 'Usuário ativado com sucesso!',
        description: 'O usuário poderá acessar o Portal novamente.',
      });
      await refetch();
    } catch (err) {
      toaster.create({
        type: 'error',
        title: 'Erro ao ativar usuário',
        description:
          err instanceof Error
            ? err.message
            : 'Não foi possível concluir a ação.',
      });
    }
  };

  const columns = useMemo<DataTableColumn<UserDto>[]>(
    () => [
      {
        accessorKey: 'name',
        header: 'Nome',
        cell: ({ row }) => {
          const user = row.original;
          return (
            <div className='flex items-center gap-3'>
              <Avatar.Root size='md' shape='circle'>
                <Avatar.Fallback />
              </Avatar.Root>
              <div className='flex flex-col gap-1'>
                <span className='font-gotham font-bold text-alba-black-500'>
                  {user.name}
                </span>
                <div className='flex items-center gap-1'>
                  <Mail className='w-4 h-4 text-alba-gray-600' />
                  <span className='text-alba-gray-600 text-xs'>
                    {user.email}
                  </span>
                </div>
              </div>
            </div>
          );
        },
      },
      {
        accessorKey: 'registerNumber',
        header: 'CPF / CNPJ',
        cell: ({ row }) => {
          const user = row.original;
          return (
            <span className='text-alba-gray-700 text-sm'>
              {formatRegisterNumber(user.registerNumber)}
            </span>
          );
        },
      },
      {
        accessorKey: 'profile',
        header: 'Perfil',
        cell: ({ row }) => {
          const user = row.original;
          const roleColorMap: Record<string, 'blue' | 'purple'> = {
            master: 'purple',
            interno: 'blue',
          };
          const fallbackRoleLabel: Record<string, string> = {
            master: 'Master',
            interno: 'Interno',
          };
          const groupLabel = getRoleLabel(user.groupName);
          const profileLabelFromProfile = getRoleLabel(user.profile);
          const profileLabel =
            groupLabel !== user.groupName
              ? groupLabel
              : fallbackRoleLabel[user.role] ||
                profileLabelFromProfile ||
                user.profile ||
                user.groupName;
          return (
            <div>
              <Tag.Root
                variant='outline'
                colorScheme={roleColorMap[user.role] || 'blue'}
                size='lg'
              >
                <Tag.Label>{profileLabel}</Tag.Label>
              </Tag.Root>
            </div>
          );
        },
      },
      {
        accessorKey: 'status',
        header: 'Status',
        cell: ({ row }) => {
          const user = row.original;
          const statusText = getUserStatus(user.status);
          const isActive = statusText !== 'Inativo';
          return (
            <div className='flex items-center gap-2'>
              <span
                className={cn(
                  'w-2.5 h-2.5 rounded-full',
                  isActive ? 'bg-alba-success-green-500' : 'bg-alba-gray-400'
                )}
              />
              <span className='text-alba-gray-700 text-sm'>
                {statusText}
              </span>
            </div>
          );
        },
      },
      {
        id: 'actions',
        header: 'Ações',
        size: 80,
        sortable: false,
        cell: ({ row }) => {
          const user = row.original;
          const isActive = getUserStatus(user.status) !== 'Inativo';

          if (!canManageStatus && !canManageUsers) return null;

          return (
            <div className='flex justify-end'>
              <Menu.Root key={`${user.email}-${user.status}`}>
                <Menu.Trigger />
                <Menu.Content>
                  {canManageUsers && (
                    <Menu.Item
                      value='edit'
                      icon={<Edit size={16} />}
                      onSelect={() => setEditingUser(user)}
                    >
                      Editar
                    </Menu.Item>
                  )}
                  {!canManageStatus ? null : isActive ? (
                    <Menu.Item
                      value='deactivate'
                      icon={<Ban size={16} />}
                      disabled={isDeactivating}
                      onSelect={() => handleDeactivateUser(user.email)}
                    >
                      Desativar
                    </Menu.Item>
                  ) : (
                    <Menu.Item
                      value='activate'
                      icon={<UserCheck size={16} />}
                      disabled={isActivating}
                      onSelect={() => handleActivateUser(user.email)}
                    >
                      Ativar
                    </Menu.Item>
                  )}
                </Menu.Content>
              </Menu.Root>
            </div>
          );
        },
      },
    ],
    [canManageStatus, canManageUsers, isDeactivating, isActivating]
  );

  return (
    <>
      <div className='mb-6'>
        <h1 className='text-xl text-alba-gray-800 mt-2'>
          Usuários do Corretor
        </h1>
        <p className='text-alba-gray-600 text-sm'>
          Gerencie os usuários vinculados a este corretor
        </p>
        {canManageUsers && createUserBlockedReason && (
          <p className='mt-1 text-sm text-alba-error-red-500'>
            {createUserBlockedReason}
          </p>
        )}
      </div>

      <PageHeader.Filters className='mt-2'>
        <div className='mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4'>
          <SearchFilterField
            {...registerFilter('search')}
            placeholder='Buscar por nome, CPF ou email...'
            className='w-full sm:w-1/3'
          />
          <SelectFilterField
            {...registerFilter('status')}
            items={profileOptions}
            placeholder='Perfil'
            onValueChange={() => setFilters({ ...filters, page: 1 })}
            className='w-full flex-shrink-0 sm:w-48'
          />
          {canManageUsers && (
            <span
              title={createUserBlockedReason}
              className='w-full sm:ml-auto sm:w-auto'
            >
              <Button
                variant='primary'
                size='md'
                disabled={!brokerDetails.isActive}
                onClick={() => setIsCreateUserModalOpen(true)}
                className='w-full sm:w-auto'
              >
                <Plus className='h-5 w-5' strokeWidth={2} />
                Adicionar usuário
              </Button>
            </span>
          )}
        </div>
      </PageHeader.Filters>

      <Page.Content>
        {error ? (
          <div className='py-8 text-center text-alba-error-red-500'>
            Erro ao carregar usuários do corretor. Tente novamente.
          </div>
        ) : (
          <>
            <div className='hidden sm:block'>
              <DataTable
                loading={isLoading}
                columns={columns}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                totalPages={usersData?.meta.totalPages || 0}
                data={usersData?.data || []}
                pageSize={filters.pageSize}
                totalItems={usersData?.meta.total || 0}
                onPageChange={(page, pageSize) =>
                  setFilters({ ...filters, page, pageSize })
                }
              />
            </div>

            <div className='flex flex-col gap-3 sm:hidden'>
              {(usersData?.data || []).map(user => {
                const statusText = getUserStatus(user.status);
                const isActive = statusText !== 'Inativo';
                const roleColorMap: Record<string, 'blue' | 'purple'> = {
                  master: 'purple',
                  interno: 'blue',
                };
                const roleLabel: Record<string, string> = {
                  master: 'Master',
                  interno: 'Interno',
                };

                return (
                  <div
                    key={user.email}
                    className='rounded-2xl border border-alba-gray-200 bg-white px-4 py-3 shadow-[0px_2px_10px_0px_rgba(15,22,30,0.06)]'
                  >
                    <div className='flex items-start justify-between gap-3'>
                      <div className='flex items-center gap-3 min-w-0'>
                        <Avatar.Root size='md' shape='circle'>
                          <Avatar.Fallback />
                        </Avatar.Root>
                        <div className='min-w-0'>
                          <p className='truncate font-gotham font-bold text-alba-black-500'>
                            {user.name}
                          </p>
                          <div className='mt-1 flex items-center gap-1'>
                            <Mail className='h-4 w-4 text-alba-gray-600' />
                            <span className='truncate text-xs text-alba-gray-600'>
                              {user.email}
                            </span>
                          </div>
                        </div>
                      </div>
                      {(canManageStatus || canManageUsers) && (
                        <Menu.Root key={`${user.email}-${user.status}`}>
                          <Menu.Trigger />
                          <Menu.Content>
                            {canManageUsers && (
                              <Menu.Item
                                value='edit'
                                icon={<Edit size={16} />}
                                onSelect={() => setEditingUser(user)}
                              >
                                Editar
                              </Menu.Item>
                            )}
                            {!canManageStatus ? null : isActive ? (
                              <Menu.Item
                                value='deactivate'
                                icon={<Ban size={16} />}
                                disabled={isDeactivating}
                                onSelect={() =>
                                  handleDeactivateUser(user.email)
                                }
                              >
                                Desativar
                              </Menu.Item>
                            ) : (
                              <Menu.Item
                                value='activate'
                                icon={<UserCheck size={16} />}
                                disabled={isActivating}
                                onSelect={() => handleActivateUser(user.email)}
                              >
                                Ativar
                              </Menu.Item>
                            )}
                          </Menu.Content>
                        </Menu.Root>
                      )}
                    </div>

                    <div className='mt-3 h-px w-full bg-alba-gray-200' />

                    <div className='mt-3 flex flex-col gap-2 text-sm'>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-alba-gray-600'>CPF / CNPJ</span>
                        <span className='text-alba-gray-700'>
                          {formatRegisterNumber(user.registerNumber)}
                        </span>
                      </div>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-alba-gray-600'>Perfil</span>
                        <Tag.Root
                          variant='outline'
                          colorScheme={roleColorMap[user.role] || 'blue'}
                          size='sm'
                        >
                          <Tag.Label>
                            {user.groupName ||
                              roleLabel[user.role] ||
                              user.profile}
                          </Tag.Label>
                        </Tag.Root>
                      </div>
                      <div className='flex items-center justify-between gap-2'>
                        <span className='text-alba-gray-600'>Status</span>
                        <div className='flex items-center gap-2'>
                          <span
                            className={`h-2.5 w-2.5 rounded-full ${
                              isActive
                                ? 'bg-alba-success-green-500'
                                : 'bg-alba-gray-400'
                            }`}
                          />
                          <span className='text-alba-gray-700'>
                            {statusText}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {!isLoading && (usersData?.data || []).length === 0 && (
                <div className='rounded-2xl border border-alba-gray-200 bg-white py-8 text-center text-alba-gray-600'>
                  Nenhum resultado encontrado.
                </div>
              )}
            </div>
          </>
        )}
      </Page.Content>

      <CreateBrokerUserModal
        isOpen={isCreateUserModalOpen}
        onClose={() => setIsCreateUserModalOpen(false)}
        onSuccess={() => {
          setIsCreateUserModalOpen(false);
          refetch();
        }}
        brokerId={brokerId}
      />

      <EditUserModal
        userId={null}
        initialUser={editingUser}
        isOpen={Boolean(editingUser)}
        onClose={() => setEditingUser(null)}
        onSuccess={() => {
          setEditingUser(null);
          refetch();
        }}
      />
    </>
  );
}
